import {
  ConfirmationCommentConfig,
  useModalBulkActionConfirmation,
  WSAlertConfig
} from "../modals/ModalBulkActionConfirmation";
import { useModalBulkActionPreparing } from "../modals/ModalBulkActionPreparing";
import { useModalBulkActionResults } from "../modals/ModalBulkActionResults";
import { useModalForegroundProcessing } from "../modals/ModalForegroundProcessing";
import { BulkActionProgress } from "../types";
import { WSActionsButton } from "@wingspanhq/fe-component-library";

export type ForegroundBulkActionConfig<T = void> = {
  actionName: string;
  itemCount: number;
  confirmDescription?: string;
  confirmLabel?: string;
  confirmDestructive?: boolean;
  /** When provided, shows a comment textarea in the confirmation step. The entered comment is passed to `process` as the third argument. */
  confirmComment?: ConfirmationCommentConfig;
  /** When provided, renders a WSAlert between the description and the comment field. */
  confirmAlert?: WSAlertConfig;
  /** When provided and at least one item succeeds, the results modal shows an extra primary button (e.g. "View scheduled payables") that navigates to where the items moved next. */
  nextAction?: WSActionsButton;
  nextSecondaryAction?: WSActionsButton;
  /** When true, skips the confirmation modal and begins processing immediately. */
  skipConfirm?: boolean;
  prepare: () => Promise<T>;
  getItemCount?: (data: T) => number;
  process: (
    data: T,
    onProgress: (progress: BulkActionProgress) => void,
    comment?: string
  ) => Promise<BulkActionProgress>;
};

export function useForegroundBulkAction() {
  const modalConfirmation = useModalBulkActionConfirmation();
  const modalPreparing = useModalBulkActionPreparing();
  const modalProcessing = useModalForegroundProcessing();
  const modalResults = useModalBulkActionResults();

  return async <T>(config: ForegroundBulkActionConfig<T>) => {
    let comment: string | undefined;

    if (!config.skipConfirm) {
      const confirmResult = await modalConfirmation.open({
        actionName: config.actionName,
        actionType: "foreground",
        itemCount: config.itemCount,
        description: config.confirmDescription,
        confirmLabel: config.confirmLabel,
        confirmDestructive: config.confirmDestructive,
        confirmComment: config.confirmComment,
        confirmAlert: config.confirmAlert
      });

      if (confirmResult === false) return;

      comment =
        typeof confirmResult === "string" && confirmResult.length > 0
          ? confirmResult
          : undefined;
    }

    modalPreparing.open({
      actionName: config.actionName,
      actionType: "foreground",
      itemCount: config.itemCount
    });

    let data: T;
    try {
      data = await config.prepare();
    } finally {
      modalPreparing.close();
    }

    const actualItemCount = config.getItemCount?.(data) ?? config.itemCount;

    const result = await modalProcessing.open({
      actionName: config.actionName,
      itemCount: actualItemCount,
      process: onProgress =>
        config.confirmComment
          ? config.process(data, onProgress, comment)
          : config.process(data, onProgress)
    });

    if (!result) return;

    await modalResults.open({
      actionName: config.actionName,
      result,
      nextAction: config.nextAction,
      secondaryAction: config.nextSecondaryAction
    });
  };
}
